import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { useAuthListener, useSession } from '@/features/auth/useSession';
import type { Role } from '@/features/identity/api';
import { useMe } from '@/features/identity/useMe';
import { takeRememberedInvite } from '@/features/relationships/pendingInvite';
import ConfirmedPage from '@/routes/ConfirmedPage';
import CurationDetailPage from '@/routes/CurationDetailPage';
import CurationPage from '@/routes/CurationPage';
import ForgotPasswordPage from '@/routes/ForgotPasswordPage';
import InvitePage from '@/routes/InvitePage';
import OnboardingPage from '@/routes/OnboardingPage';
import ProfessionalProfilePage from '@/routes/ProfessionalProfilePage';
import ProfilePage from '@/routes/ProfilePage';
import PublicProfessionalPage from '@/routes/PublicProfessionalPage';
import StudentDetailPage from '@/routes/StudentDetailPage';
import StudentProfilePage from '@/routes/StudentProfilePage';
import StudentsPage from '@/routes/StudentsPage';
import PlanEditorPage from '@/routes/PlanEditorPage';
import StudentPlansPage from '@/routes/StudentPlansPage';
import TemplateEditorPage from '@/routes/TemplateEditorPage';
import TemplatesPage from '@/routes/TemplatesPage';
import ResetPasswordPage from '@/routes/ResetPasswordPage';
import SignInPage from '@/routes/SignInPage';
import SignUpPage from '@/routes/SignUpPage';
import TodayPage from '@/routes/TodayPage';

/**
 * As rotas são em inglês de propósito.
 *
 * Com três idiomas, traduzir caminhos criaria três URLs para a mesma tela — e
 * um link compartilhado entre um aluno em português e um em espanhol deixaria
 * de abrir. O caminho é identificador, não texto de interface.
 */

function Loading() {
  const { t } = useTranslation(['common']);
  return (
    <div className="mh-shell items-center justify-center" aria-busy="true">
      <p className="text-(--color-sand-700)">{t('common:state.loading')}</p>
    </div>
  );
}

function RequireSession({ children }: { children: ReactNode }) {
  const { status } = useSession();

  // O estado `loading` importa: sem ele, a primeira renderização — antes de o
  // Supabase devolver a sessão do armazenamento — mandaria todo mundo para o
  // login, inclusive quem já estava autenticado.
  if (status === 'loading') return <Loading />;
  if (status === 'anonymous') return <Navigate to="/sign-in" replace />;

  return <>{children}</>;
}

/**
 * Quem ainda não concluiu o onboarding vai para ele.
 *
 * **Um erro ao consultar `/me` não prende ninguém aqui.** Se a API estiver
 * fora, a pessoa segue para a aplicação e encontra o erro na tela que tentou
 * abrir — em vez de ficar num redirecionamento que ela não tem como resolver.
 */
function RequireOnboarding({ children }: { children: ReactNode }) {
  const { data: me, isPending, isError } = useMe();

  if (isPending) return <Loading />;
  if (isError) return <>{children}</>;
  if (me && me.onboarding_status !== 'completed') return <Navigate to="/onboarding" replace />;

  return <>{children}</>;
}

/**
 * Rota de papel: quem não tem o papel volta para o perfil.
 *
 * Não é segurança — a API recusa a gravação de qualquer forma, e é ela que
 * decide. É evitar que alguém chegue por link a um formulário que não pode
 * salvar e só descubra depois de preencher.
 */
function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { data: me, isPending } = useMe();

  if (isPending) return <Loading />;
  if (me && !me.roles.includes(role)) return <Navigate to="/profile" replace />;

  return <>{children}</>;
}

/**
 * Retoma o convite que ficou esperando durante o cadastro.
 *
 * Quem chega por um link de convite sem conta passa por cadastro, confirmação
 * de e-mail e onboarding — três telas que trocam a URL. Sem isto, ao final de
 * tudo a pessoa cairia na tela Hoje e o convite estaria perdido: o token vivia
 * numa URL que ninguém mais tem.
 */
function ResumeInvite({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Não retoma estando na própria tela do convite — seria um laço.
    if (pathname.startsWith('/convite/')) return;
    const token = takeRememberedInvite();
    if (token) navigate(`/convite/${token}`, { replace: true });
  }, [pathname, navigate]);

  return <>{children}</>;
}

function Placeholder({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation(['common']);
  return (
    <div className="px-5 py-6">
      <h1 className="text-3xl">{t(titleKey)}</h1>
      <p className="mt-2 text-(--color-sand-700)">{t('common:state.empty')}</p>
    </div>
  );
}

export default function App() {
  useAuthListener();

  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* As duas recebem `token_hash` do e-mail e o trocam por uma sessão.
          Ficam fora de `RequireSession` de propósito: quem chega aqui ainda
          não tem sessão — é justamente o que a tela vai criar. */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/welcome" element={<ConfirmedPage />} />

      {/* Fora de `RequireSession`: mostrar quem convidou **antes** de pedir
          login é o que evita que um link de convite pareça golpe. A própria
          tela conduz ao cadastro quando for o caso. */}
      <Route path="/convite/:token" element={<InvitePage />} />

      {/* A vitrine do profissional. Único caminho do produto que responde a
          quem não tem conta — e é curto de propósito: `/p/:slug`, para caber
          num cartão e ser ditado por telefone sem erro. */}
      <Route path="/p/:slug" element={<PublicProfessionalPage />} />

      {/* Exige sessão, mas **não** exige onboarding concluído — seria um laço:
          a tela que conclui o onboarding não pode exigir onboarding. */}
      <Route
        path="/onboarding"
        element={
          <RequireSession>
            <OnboardingPage />
          </RequireSession>
        }
      />

      <Route
        element={
          <RequireSession>
            <RequireOnboarding>
              <ResumeInvite>
                <AppShell />
              </ResumeInvite>
            </RequireOnboarding>
          </RequireSession>
        }
      >
        <Route path="/today" element={<TodayPage />} />
        <Route path="/plan" element={<Placeholder titleKey="common:nav.plan" />} />
        <Route path="/messages" element={<Placeholder titleKey="common:nav.messages" />} />
        <Route path="/profile" element={<ProfilePage />} />
        {/* Cada perfil de papel na sua rota, e não em abas dentro de `/profile`.
            Quem tem os dois papéis alterna entre duas telas curtas em vez de
            rolar uma longa; quem tem um só nunca vê a outra. */}
        <Route
          path="/profile/student"
          element={
            <RequireRole role="student">
              <StudentProfilePage />
            </RequireRole>
          }
        />
        <Route
          path="/profile/professional"
          element={
            <RequireRole role="professional">
              <ProfessionalProfilePage />
            </RequireRole>
          }
        />
        <Route
          path="/students"
          element={
            <RequireRole role="professional">
              <StudentsPage />
            </RequireRole>
          }
        />
        <Route
          path="/students/:relationshipId"
          element={
            <RequireRole role="professional">
              <StudentDetailPage />
            </RequireRole>
          }
        />

        {/* Curadoria do catálogo. `admin` não é papel que se escolhe — é
            designado no banco —, então quem chegar aqui por link sem tê-lo
            volta para o perfil, e a API recusaria de qualquer forma. */}
        {/* O plano pendura no vínculo, e a rota também: é assim que o
            profissional pensa — "os planos do Bruno", não "todos os planos". */}
        <Route
          path="/students/:relationshipId/plans"
          element={
            <RequireRole role="professional">
              <StudentPlansPage />
            </RequireRole>
          }
        />
        <Route
          path="/plans/:planId"
          element={
            <RequireRole role="professional">
              <PlanEditorPage />
            </RequireRole>
          }
        />

        {/* Modelos de treino: material de trabalho do profissional, nunca
            visto pelo aluno. */}
        <Route
          path="/templates"
          element={
            <RequireRole role="professional">
              <TemplatesPage />
            </RequireRole>
          }
        />
        <Route
          path="/templates/:templateId"
          element={
            <RequireRole role="professional">
              <TemplateEditorPage />
            </RequireRole>
          }
        />

        <Route
          path="/curation"
          element={
            <RequireRole role="admin">
              <CurationPage />
            </RequireRole>
          }
        />
        <Route
          path="/curation/:exerciseId"
          element={
            <RequireRole role="admin">
              <CurationDetailPage />
            </RequireRole>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/today" replace />} />
    </Routes>
  );
}
